import { useCallback, useState } from 'react';

const LIMIT = 100;

interface History<T> {
  readonly past: readonly T[];
  readonly present: T;
  readonly future: readonly T[];
}

/**
 * Undo / redo of unsaved draft edits (memory only). `change` records a step; `annotate` updates
 * the present without a step (server classifications after a preview); `reset` starts over (after
 * a save or a reload). At most 100 steps are kept.
 */
export function useHistory<T>(initial: () => T) {
  const [history, setHistory] = useState<History<T>>(() => ({
    past: [],
    present: initial(),
    future: [],
  }));
  const change = useCallback((update: (current: T) => T) => {
    setHistory((h) => {
      const next = update(h.present);
      return next === h.present
        ? h
        : { past: [...h.past.slice(-(LIMIT - 1)), h.present], present: next, future: [] };
    });
  }, []);
  const annotate = useCallback((update: (current: T) => T) => {
    setHistory((h) => ({ ...h, present: update(h.present) }));
  }, []);
  const undo = useCallback(() => {
    setHistory((h) => {
      const previous = h.past.at(-1);
      return previous === undefined
        ? h
        : { past: h.past.slice(0, -1), present: previous, future: [h.present, ...h.future] };
    });
  }, []);
  const redo = useCallback(() => {
    setHistory((h) => {
      const [next, ...rest] = h.future;
      return next === undefined ? h : { past: [...h.past, h.present], present: next, future: rest };
    });
  }, []);
  const reset = useCallback((value: T) => {
    setHistory({ past: [], present: value, future: [] });
  }, []);
  return {
    value: history.present,
    canUndo: history.past.length > 0,
    canRedo: history.future.length > 0,
    change,
    annotate,
    undo,
    redo,
    reset,
  };
}
